// @ts-nocheck
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Grid, Typography, MenuItem, FormControlLabel } from '@mui/material';
import moment from 'moment';

import {
  FButtonDropdown,
  FHConfirmDialog,
  FHRadio,
  FHRadioGroup,
  FHSkeletonLoader,
} from '../../../base';
import { sessionActions } from '../../../../store';
import { formatEntityId, history, areUserRolesIn } from '../../../../helpers';
import { getSessionStatus } from '../../../../utils/common';
import {
  FORTA_GRAY4,
  FORTA_GRAY3,
  FORTA_GRAY2,
  FORTA_WHITE,
  FORTA_SUCCESS,
  FORTA_YELLOW,
  FORTA_ERROR,
  FORTA_BLUE,
} from '../../../../utils/colors';
import { Roles, BTSessionStatus, Themes } from '../../../../constants';
import { CQAReviewStatus, ReasonForDeletion } from '../constants';
import ViewNotificationIcon from '../../../svgs/ViewNotificationIcon';
import ClientIcon from '../../../../assets/images/client-card.svg';
import BtIcon from '../../../../assets/images/bt-light.svg';
import MoreIcon from '../../../../assets/images/more.svg';
import SignatureIcon from '../../../../assets/images/signature.svg';
import QASessionNoteIcon from '../../../svgs/QASessionNoteIcon';
import RemoveIcon from '../../../svgs/RemoveIcon';

import type { RootState } from '../../../../store';
import type { ChangeEvent } from 'react';
import type { ISessionMinifiedWithClaimHistory } from '../../../../utils/types';

const DropdownIcon = () => (
  <Box sx={{ padding: 1 }}>
    <img data-testid="qaCardMoreIcon" src={MoreIcon} alt="more icon" />
  </Box>
);

interface ISessionNotesQACardProps {
  loading: boolean;
  session: ISessionMinifiedWithClaimHistory;
  updateSessions: () => void;
}

function SessionNotesQACard(props: ISessionNotesQACardProps) {
  const {
    updateSessions,
    loading,
    session,
    session: { client, session_provider, date_start, clinical_qa_review, id, claim_history },
  } = props;
  const isClaimed = claim_history.length > 0;
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [reasonForDeletion, setReasonForDeletion] = useState<ReasonForDeletion | null>(null);
  const sessionStatus = getSessionStatus(session);

  const handleViewNotificationsClick = (e: any) => {
    e.stopPropagation();
    history.navigate(`/sessions/${id}/review`);
  };

  const handleQASessionNoteClick = (e: any) => {
    e.stopPropagation();
    history.navigate(`/sessions/${id}`);
  };

  const handleDeleteSessionReviewMenuClick = (_e: any) => {
    setShowConfirm(true);
  };

  const handleDeleteSession = async (e: any) => {
    e.stopPropagation();
    const response = await dispatch(
      sessionActions.deleteClinicalReview({
        sessionId: id,
        clinicalQAReviewId: clinical_qa_review?.id,
        getAccessTokenSilently,
        data: {
          reason: reasonForDeletion,
          date_deleted: new Date().toISOString(),
        },
      }),
    );
    if (!response.error) updateSessions();
    setShowConfirm(false);
  };

  const handleQACardClick = () => {
    const { id, claim_history } = props.session;
    const sessionQAStatus = getSessionStatus(props.session);
    if (claim_history.length) return;

    if (sessionQAStatus?.statusId === BTSessionStatus.AwaitingReview)
      history.navigate(`/sessions/${id}`);
    else if (
      sessionQAStatus?.statusId === BTSessionStatus.Completed ||
      sessionQAStatus?.statusId === BTSessionStatus.EditsRequested
    )
      history.navigate(`/sessions/${id}/review`);
  };

  const renderDeleteOptions = () => {
    return (
      <Box mt={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
          <RemoveIcon />
          <Typography color={FORTA_BLUE.primary} variant="h4" sx={{ ml: 1, fontWeight: '500' }}>
            Reason for deletion
          </Typography>
        </Box>
        <Typography
          color={FORTA_BLUE.primary}
          sx={{
            mb: 3,
            fontWeight: '400',
            lineHeight: '21px',
            fontSize: '14px',
            color: FORTA_GRAY3,
          }}
        >
          Please select a reason to delete session note here
        </Typography>
        <FHRadioGroup
          rowGap="16px"
          colorTheme={Themes.Primary}
          coloredBackground
          name="reason-for-deletion"
          value={reasonForDeletion}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            if (
              e.target.value === ReasonForDeletion.Correction ||
              e.target.value === ReasonForDeletion.Incomplete
            ) {
              setReasonForDeletion(e.target.value);
            }
          }}
          sx={{ flexDirection: 'row', mb: 1 }}
        >
          <FormControlLabel
            sx={{
              width: 'fit-content',
              pr: 6,
              fontSize: '16px',
              lineHeight: '24px',
              fontWeight: '400',
            }}
            value={ReasonForDeletion.Correction}
            control={<FHRadio colorTheme={Themes.Primary} />}
            label="Correction"
            data-testid="reasonForDeletionCorrectionOption"
          />
          <FormControlLabel
            sx={{ width: 'fit-content', fontSize: '16px', lineHeight: '24px', fontWeight: '400' }}
            value={ReasonForDeletion.Incomplete}
            control={<FHRadio colorTheme={Themes.Primary} />}
            label="Incomplete"
            data-testid="reasonForDeletionIncompleteOption"
          />
        </FHRadioGroup>
      </Box>
    );
  };

  const isAwaitingFinalization =
    sessionStatus?.statusId === BTSessionStatus.AwaitingReview &&
    clinical_qa_review?.status === CQAReviewStatus.Acknowledged;

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        backgroundColor: FORTA_WHITE,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: 2,
        cursor: isClaimed ? 'not-allowed' : 'pointer',
      }}
      onClick={() => {
        handleQACardClick();
      }}
    >
      <Grid container spacing={2} justifyContent="space-between">
        <Grid
          item
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flex: 1,
          }}
        >
          <Grid container spacing={2} justifyContent="space-between">
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
                flex: 1,
              }}
            >
              <Box>
                {loading ? (
                  <FHSkeletonLoader variant="circular" width={40} height={40} />
                ) : (
                  <img src={sessionStatus?.icon} alt="status icon" height={40} width={40} />
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  flexGrow: 1,
                  gap: 1,
                }}
              >
                {loading ? (
                  <FHSkeletonLoader
                    height={15}
                    rowCount={1}
                    width={'200px'}
                    containerStyles={{ mb: 2 }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.75,
                    }}
                  >
                    <Typography color={FORTA_GRAY3} align="left">
                      {moment(date_start).format('MM/DD/YYYY')}
                    </Typography>
                    -
                    <Typography color={FORTA_GRAY3} sx={{ fontWeight: 700 }}>
                      {formatEntityId(String(session?.id), 'F')}
                    </Typography>
                    {isClaimed && (
                      <>
                        -
                        <Typography color={FORTA_BLUE.bright} sx={{ fontWeight: 700 }}>
                          {claim_history[0].status}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', width: '100%' }}>
                  {loading ? (
                    <FHSkeletonLoader width={'200px'} />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexGrow: 1,
                        gap: 1,
                      }}
                    >
                      <img src={ClientIcon} alt="check icon" />
                      <Typography color={FORTA_GRAY4} align="left">
                        {client?.first_name} {client?.last_name} ({client?.id})
                      </Typography>
                    </Box>
                  )}
                  {loading ? (
                    <FHSkeletonLoader width={'200px'} containerStyles={{ ml: 4 }} />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                        py: 0,
                        px: 1,
                      }}
                    >
                      <img src={BtIcon} alt="bt icon" />
                      <Typography color={FORTA_GRAY4} align="left">
                        {session_provider?.first_name} {session_provider?.last_name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
            <Box
              sx={{
                pt: 2,
                pr: 1,
                pb: 0,
                pl: 2,
              }}
            >
              <Divider
                sx={{
                  color: FORTA_GRAY2,
                  borderWidth: '1.5px',
                  borderRadius: '16px',
                }}
                orientation="vertical"
              />
            </Box>
            {Boolean(clinical_qa_review) &&
            clinical_qa_review?.status !== CQAReviewStatus.InProgress ? (
              <Grid
                item
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  minHeight: '54px',
                  gap: 2,
                  flex: 1,
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1,
                    minWidth: '160px',
                  }}
                >
                  {loading ? (
                    <FHSkeletonLoader width={'100px'} containerStyles={{ mb: 2 }} />
                  ) : (
                    <Typography color={FORTA_GRAY3} align="left">
                      {isAwaitingFinalization ? 'Awaiting finalization' : 'Review'}
                    </Typography>
                  )}

                  {loading ? (
                    <FHSkeletonLoader width={'200px'} />
                  ) : (
                    <Typography color={FORTA_GRAY4} align="left">
                      {clinical_qa_review?.date_reviewed
                        ? moment(clinical_qa_review.date_reviewed).format('MM/DD/YYYY - h:mm a')
                        : ''}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: 3,
                    width: '134px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    {loading ? (
                      <FHSkeletonLoader width={'60px'} />
                    ) : (
                      <Typography color={FORTA_GRAY3} align="left" width={'88px'}>
                        {clinical_qa_review?.status === 'ACKNOWLEDGED' ? 'Signed' : 'Not Signed'}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 3,
                      }}
                    >
                      {loading ? (
                        <FHSkeletonLoader variant="circular" width={24} height={24} />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: 24,
                            height: 24,
                            backgroundColor:
                              clinical_qa_review?.status === 'ACKNOWLEDGED'
                                ? FORTA_SUCCESS
                                : FORTA_YELLOW,
                            borderRadius: '50%',
                          }}
                        >
                          <img src={SignatureIcon} alt="Signature icon" />
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    {loading ? (
                      <FHSkeletonLoader width={'60px'} />
                    ) : (
                      <Typography color={FORTA_GRAY3} align="center">
                        {clinical_qa_review?.verdict}
                      </Typography>
                    )}
                    {loading ? (
                      <FHSkeletonLoader variant="circular" width={24} height={24} />
                    ) : (
                      <Typography
                        color={clinical_qa_review?.verdict === 'Fail' ? FORTA_ERROR : FORTA_SUCCESS}
                        align="center"
                        sx={{
                          fontSize: '21px',
                          fontWeight: 700,
                        }}
                      >
                        {clinical_qa_review?.score}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            ) : (
              <Grid
                item
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'start',
                  minHeight: '54px',
                  minWidth: '320px',
                  gap: 2,
                  justifyContent: 'end',
                  flex: 1,
                }}
              >
                {loading ? (
                  <FHSkeletonLoader width={'120px'} />
                ) : (
                  <Typography color={FORTA_GRAY3} align="left">
                    {!clinical_qa_review ? 'Awaiting review' : 'Review in progress'}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <FButtonDropdown
              buttonRenderer={DropdownIcon}
              svgHoverStyles={{
                '& > g > path': {
                  fill: FORTA_WHITE,
                },
                '& > path': {
                  stroke: FORTA_WHITE,
                },
              }}
              disabled={loading}
              dataTestId={`qaSessionReviewCard${id}`}
            >
              <MenuItem
                onClick={handleQASessionNoteClick}
                disabled={sessionStatus?.statusId !== BTSessionStatus.AwaitingReview || isClaimed}
                data-testid="qaSessionNote"
              >
                <Box ml={-2.4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <QASessionNoteIcon />
                  <Typography ml={1}>
                    {isAwaitingFinalization ? 'Finalize note' : 'QA Session note'}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={handleViewNotificationsClick}
                disabled={
                  sessionStatus?.statusId === BTSessionStatus.AwaitingReview &&
                  !isAwaitingFinalization
                }
                data-testid="viewNotifications"
              >
                <Box ml={-2.4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <ViewNotificationIcon />
                  <Typography ml={1}>View notifications</Typography>
                </Box>
              </MenuItem>
              {areUserRolesIn(user?.roles, [Roles.Admin]) && clinical_qa_review && (
                <MenuItem
                  onClick={handleDeleteSessionReviewMenuClick}
                  data-testid="deleteSessionReview"
                >
                  <Box ml={-2.4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <RemoveIcon />
                    <Typography ml={1}>Delete session review</Typography>
                  </Box>
                </MenuItem>
              )}
            </FButtonDropdown>
          </Box>
        </Grid>
      </Grid>
      <FHConfirmDialog
        open={showConfirm}
        colorTheme={Themes.Primary}
        title={''}
        submittext={'Submit & delete'}
        hideCancelButton={true}
        onClose={() => setShowConfirm(false)}
        onSubmit={handleDeleteSession}
        contentRenderer={renderDeleteOptions}
        onClick={(e) => {
          e.stopPropagation();
        }}
        disableSubmitButton={!reasonForDeletion}
        dialogStyles={{ padding: '16px 24px', height: '248px' }}
        submitButtonStyles={{
          fontSize: '21px',
          lineHeight: '38px',
          fontWeight: '700',
          padding: '8px 16px',
        }}
        submitBtnTestId={'submitReviewDelete'}
      />
    </Box>
  );
}

export default SessionNotesQACard;
